import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import {
  ArrowChevronLeftIcon,
  CurecallBanner,
  CurecallButton,
  CurecallInput,
  CurecallMultiSelect,
  ECurecallButtonAppearance,
  TOptionWithNumberValue,
  TOptionWithStringValue,
} from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react'
import { EHumanReviewRequestSubject } from 'src/pages/dashboard/_components/human-review-requests.type'
import { formatCombinedIsoDuration } from 'src/utils/duration-iso.util'
import { useTranslation } from 'react-i18next'
import { useCurrentProject, WithFeatureEnabled } from 'src/providers/current-project.provider'
import MessageFields from './message-fields.component'
import DashboardSection from './dashboard-section.component'
import ScheduleFields from './schedule-fields.component'
import { TFollowup } from 'src/types/followup'
import { TMedEventUI } from 'src/types/med-event'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useLocale } from 'src/hooks/use-locale'
import { TLabel } from 'src/types/locales'
import { useNotification } from 'src/providers/notification.provider'
import { ProjectService } from 'src/services/project.service'
import { useQueryClient } from '@tanstack/react-query'
import {
  EProgrammedMessageCategories,
  TProgrammedMessage,
  TProgrammedMessageFormValues,
  TProgrammedMessageForAPI,
} from './programmed-message.type'
import SchedulingOptions from './scheduling-options.component'
import SendingRules from './sending-rules.component'
import ProgrammedMessageRecap from './programmed-message-recap.component'
import { isConflictError } from 'src/utils/http.util'

function FormStep({
  number,
  title,
  subtitle,
  children,
}: {
  number: number
  title: string
  subtitle: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div className="c-flex c-flex-col c-border c-rounded-xl c-border-grey-400 c-shadow-md">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={isOpen ? t('common.actions.collapse') : t('common.actions.expand')}
        className={twMerge(
          'c-flex c-items-start c-p-4 c-rounded-t-xl c-gap-4 c-text-left hover:c-bg-grey-50',
          !isOpen && 'c-rounded-xl',
        )}
      >
        <div className="c-flex c-h-8 c-w-8 c-shrink-0 c-items-center c-justify-center c-rounded-full c-bg-grey-700 c-font-nexa c-text-sm c-font-bold c-text-white">
          {number}
        </div>
        <div className="c-flex-1">
          <h2 className="c-m-0 c-font-nexa c-text-base c-font-bold c-text-grey-900">{title}</h2>
          <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">{subtitle}</p>
        </div>
        <div className="c-flex c-h-8 c-w-8 c-shrink-0 c-items-center c-justify-center c-text-grey-600">
          <ArrowChevronLeftIcon
            className={twMerge('c-h-4 c-w-4 c-transition-transform', isOpen ? '-c-rotate-90' : 'c-rotate-90')}
          />
        </div>
      </button>
      {isOpen && <div className="c-flex c-flex-col c-gap-6 c-p-4">{children}</div>}
    </div>
  )
}

export type { TProgrammedMessageFormValues, TProgrammedMessageForAPI }

const CATEGORY_TO_SUBJECT: Partial<Record<EProgrammedMessageCategories, EHumanReviewRequestSubject>> = {
  [EProgrammedMessageCategories.CONVOCATION]: EHumanReviewRequestSubject.CONVOCATION,
  [EProgrammedMessageCategories.POST_OPERATIVE_INSTRUCTIONS]: EHumanReviewRequestSubject.POST_OP_CONTROL,
}

function ProgrammedMessageForm({
  setIsButtonDisabled,
}: {
  setIsButtonDisabled: Dispatch<SetStateAction<boolean>>
}) {
  const { t } = useTranslation()
  const {
    formState: { errors, defaultValues },
    watch,
    setValue,
    clearErrors,
    control,
    register,
  } = useFormContext()
  const { followups, getMedEventLabelById } = useCurrentProject()

  const [medEventOptions, setMedEventOptions] = useState<TOptionWithNumberValue[]>([])
  const [selectedMedEvents, setSelectedMedEvents] = useState<TOptionWithNumberValue[]>([])
  const [selectedCategories, setSelectedCategories] = useState<TOptionWithStringValue[]>([])
  const [dashboardAnimationKey, setDashboardAnimationKey] = useState(0)

  const categoryOptions = useMemo<TOptionWithStringValue[]>(
    () => [
      {
        value: EProgrammedMessageCategories.PRE_OPERATIVE_INSTRUCTIONS,
        label: t('pages.configuration.messages.category-pre-operative-instructions'),
      },
      {
        value: EProgrammedMessageCategories.CONVOCATION,
        label: t('pages.configuration.messages.category-convocation'),
      },
      {
        value: EProgrammedMessageCategories.POST_OPERATIVE_INSTRUCTIONS,
        label: t('pages.configuration.messages.category-post-operative-instructions'),
      },
      {
        value: EProgrammedMessageCategories.SATISFACTION,
        label: t('pages.configuration.messages.category-satisfaction'),
      },
      {
        value: EProgrammedMessageCategories.WELCOME_MESSAGE,
        label: t('pages.configuration.messages.category-welcome-message'),
      },
      {
        value: EProgrammedMessageCategories.PRE_ADMISSION,
        label: t('pages.configuration.messages.category-pre-admission'),
      },
      {
        value: EProgrammedMessageCategories.OTHER,
        label: t('pages.configuration.messages.category-other'),
      },
    ],
    [t],
  )

  useEffect(() => {
    const followupId = defaultValues?.followupId
    if (followupId) {
      setValue('followupId', followupId)
      const correspondingFollowup = followups.find((followup: TFollowup) => followup.id === followupId)
      if (correspondingFollowup) {
        const medEventsOptionsAvailable =
          correspondingFollowup.configUI?.medEventsUI?.map((medEventUI: TMedEventUI) => ({
            value: medEventUI.MedEvent.id,
            label: getMedEventLabelById(medEventUI.MedEvent.id),
          })) || []
        setMedEventOptions(medEventsOptionsAvailable)
        setIsButtonDisabled(medEventsOptionsAvailable.length === 0)
        const preSelected =
          medEventsOptionsAvailable.length === 1
            ? medEventsOptionsAvailable
            : medEventsOptionsAvailable.filter((medEvent) =>
                defaultValues?.medEventIds?.includes(medEvent.value),
              )
        setSelectedMedEvents(preSelected)
        if (preSelected.length > 0) {
          setValue(
            'medEventIds',
            preSelected.map((m) => m.value),
          )
        }
      }
    }
    if (defaultValues?.categories && defaultValues.categories.length > 0) {
      const preSelected = categoryOptions.filter((option) => defaultValues.categories?.includes(option.value))
      setSelectedCategories(preSelected)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeMedEvents = (choosenMedEvents: TOptionWithNumberValue[]) => {
    clearErrors('medEventIds')
    if (choosenMedEvents) {
      setSelectedMedEvents(choosenMedEvents)
      setValue(
        'medEventIds',
        choosenMedEvents.map((medEventOption) => medEventOption.value),
      )
    }
  }

  const handleChangeCategories = (selected: TOptionWithStringValue[]) => {
    const prevCategoryValues = selectedCategories.map((opt) => opt.value as EProgrammedMessageCategories)
    const newlyAdded = selected
      .map((opt) => opt.value as EProgrammedMessageCategories)
      .filter((cat) => !prevCategoryValues.includes(cat))

    if (selected.length > 0) clearErrors('categories')
    setSelectedCategories(selected)
    setValue(
      'categories',
      selected.map((option) => option.value as EProgrammedMessageCategories),
    )

    for (const cat of newlyAdded) {
      const matchedSubject = CATEGORY_TO_SUBJECT[cat]
      if (matchedSubject !== undefined) {
        setValue('humanReviewCheck.subject', matchedSubject, { shouldDirty: true })
        clearErrors('humanReviewCheck.subject')
        if (!watch('humanReviewCheck.unreceivedSmsDelay')) {
          setValue(
            'humanReviewCheck.unreceivedSmsDelay',
            formatCombinedIsoDuration({ days: 0, hours: 1, minutes: 0 }),
          )
        }
        setDashboardAnimationKey((k) => k + 1)
        break
      }
    }
  }

  return (
    <div className="c-relative c-flex c-flex-col c-gap-4">
      <FormStep
        number={1}
        title={t('pages.configuration.messages.step-1-title')}
        subtitle={t('pages.configuration.messages.step-1-subtitle')}
      >
        {!!watch('followupId') &&
          (medEventOptions.length === 0 ? (
            <CurecallBanner appearance="WARNING">
              {t('pages.configuration.messages.impossible-to-programme')}
            </CurecallBanner>
          ) : (
            <Controller
              control={control}
              name="medEventIds"
              rules={{ required: true }}
              render={() => (
                <CurecallMultiSelect
                  items={medEventOptions}
                  selectedItems={selectedMedEvents}
                  label={t('pages.configuration.messages.med-events-list')}
                  selectedElementsLabel={
                    selectedMedEvents.length + ' ' + t('pages.configuration.messages.selected')
                  }
                  onSelect={handleChangeMedEvents}
                  placeholder={t('pages.configuration.messages.choose-med-events')}
                  tooltipContent={t('pages.configuration.messages.med-events-tooltip')}
                  tooltipPosition="right"
                  isRequired={true}
                  errorMessage={
                    errors['medEventIds'] && t('pages.configuration.messages.med-events-error-message')
                  }
                  openOptionsAriaLabel={t('pages.configuration.messages.med-events-list-open-aria-label')}
                  closeOptionsAriaLabel={t('pages.configuration.messages.med-events-list-close-aria-label')}
                  wrapperClassName="c-w-[500px] c-relative"
                  errorClassName="c-text-nowrap"
                />
              )}
            />
          ))}

        <CurecallInput
          {...register('messageName', { required: true })}
          label={t('pages.configuration.messages.message-name')}
          placeholder={t('pages.configuration.messages.name-your-message')}
          isRequired={true}
          wrapperClassName="c-w-[500px] c-relative"
          tooltipContent={t('pages.configuration.messages.message-name-tooltip')}
          tooltipPosition="right"
          errorMessage={
            errors?.['messageName'] && t('pages.configuration.messages.message-name-error-message')
          }
        />

        <Controller
          control={control}
          name="categories"
          rules={{ validate: (val) => val?.length > 0 }}
          render={() => (
            <CurecallMultiSelect
              items={categoryOptions}
              selectedItems={selectedCategories}
              onSelect={handleChangeCategories}
              label={t('pages.configuration.messages.message-categories')}
              selectedElementsLabel={
                selectedCategories.length + ' ' + t('pages.configuration.messages.selected')
              }
              placeholder={t('pages.configuration.messages.message-categories-placeholder')}
              isRequired={true}
              errorMessage={
                errors?.['categories'] && t('pages.configuration.messages.message-categories-error-message')
              }
              wrapperClassName="c-w-[500px] c-relative"
            />
          )}
        />

        <WithFeatureEnabled featuresAccepted={['dashboard', 'dashboard-v2']}>
          <DashboardSection animationKey={dashboardAnimationKey} />
        </WithFeatureEnabled>
      </FormStep>

      <FormStep
        number={2}
        title={t('pages.configuration.messages.step-2-title')}
        subtitle={t('pages.configuration.messages.step-2-subtitle')}
      >
        <ScheduleFields />
        <SchedulingOptions />
        <SendingRules />
      </FormStep>

      <FormStep
        number={3}
        title={t('pages.configuration.messages.step-3-title')}
        subtitle={t('pages.configuration.messages.step-3-subtitle')}
      >
        <MessageFields />
      </FormStep>

      <ProgrammedMessageRecap />
    </div>
  )
}

function AddProgrammedMessageForm({
  defaultFollowupId,
  onNameChange,
}: {
  defaultFollowupId?: number
  onNameChange?: (name: string) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const methods = useForm<TProgrammedMessageFormValues>({
    defaultValues: {
      categories: [],
      schedule: {
        durationDiff: '',
        atTime: undefined,
        avoidedWeekdays: [6, 7],
        avoidPublicHolidays: true,
        avoidedDates: [],
        preventRescheduleOnceTriggered: true,
        gracePeriodDuration: null,
      },
      profileRules: [{ message: { [locale]: '' } as TLabel, medicalStaffMemberId: null, ageGroup: null }],
      humanReviewCheck: null,
      ...(defaultFollowupId !== undefined && { followupId: defaultFollowupId }),
    },
  })
  const { handleSubmit, watch: watchMethods } = methods
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false)
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!onNameChange) return
    const subscription = watchMethods((value, { name }) => {
      if (name === 'messageName')
        onNameChange(value.messageName || t('pages.configuration.messages.creating-message'))
    })
    return () => subscription.unsubscribe()
  }, [watchMethods, onNameChange])

  const onSubmit = async (formData: TProgrammedMessageFormValues) => {
    setIsButtonLoading(true)
    try {
      const result = await ProjectService.postSelfConfigurationMessage(project.id, {
        followupId: formData.followupId,
        medEventIds: formData.medEventIds,
        schedule: formData.schedule,
        messageName: formData.messageName,
        profileRules: formData.profileRules,
        categories: formData.categories,
        humanReviewCheck: formData.humanReviewCheck?.subject ? formData.humanReviewCheck : null,
      })
      if (queryClient.getQueryData(['programmed-messages', project.id]) !== undefined) {
        queryClient.setQueryData(['programmed-messages', project.id], (oldData: any) => {
          return [...(oldData || []), result]
        })
      }
      notify(
        {
          title: t('pages.configuration.messages.submit-success-title', { isUpdating: false }),
          message: t('pages.configuration.messages.submit-success-message'),
          appearance: 'SUCCESS',
        },
        15000,
      )
      navigate(`/${project.id}/configuration/followups/${formData.followupId}`)
    } catch (err: any) {
      notify(
        {
          title: t('pages.configuration.messages.submit-error', { isUpdating: false }),
          message: err,
          appearance: 'ERROR',
        },
        15000,
      )
    } finally {
      setTimeout(() => {
        setIsButtonLoading(false)
      }, 2000)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProgrammedMessageForm setIsButtonDisabled={setIsButtonDisabled} />

        <div className="c-mt-16 c-pb-10 c-flex c-justify-center c-gap-4">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            type="button"
            isDisabled={isButtonDisabled}
            isLoading={isButtonLoading}
            onClick={() => navigate(-1)}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton type="submit" isDisabled={isButtonDisabled} isLoading={isButtonLoading}>
            {t('pages.configuration.messages.schedule-a-message')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}

function UpdateProgrammedMessageForm({
  defaultValues,
  messageId,
  followupId,
}: {
  defaultValues: TProgrammedMessageFormValues
  messageId: number
  followupId: string
}) {
  const { t } = useTranslation()
  const methods = useForm<TProgrammedMessageFormValues>({
    defaultValues: {
      ...defaultValues,
      schedule: {
        ...defaultValues.schedule,
        avoidedWeekdays: defaultValues.schedule.avoidedWeekdays || [6, 7],
        avoidedDates: defaultValues.schedule.avoidedDates || [],
        gracePeriodDuration: defaultValues.schedule.gracePeriodDuration || null,
      },
    },
  })
  const { handleSubmit } = methods
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false)
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const backUrl = `/${project.id}/configuration/followups/${followupId}`

  const onSubmit = async (formData: TProgrammedMessageFormValues) => {
    try {
      const result = await ProjectService.putSelfConfigurationMessage(project.id, messageId, {
        followupId: formData.followupId,
        medEventIds: formData.medEventIds,
        schedule: formData.schedule,
        messageName: formData.messageName,
        profileRules: formData.profileRules,
        categories: formData.categories,
        humanReviewCheck: formData.humanReviewCheck?.subject ? formData.humanReviewCheck : null,
      })
      if (queryClient.getQueryData(['programmed-messages', project.id]) !== undefined) {
        queryClient.setQueryData(['programmed-messages', project.id], (oldData: any) => {
          if (!oldData) return
          const updatedData = [...oldData.filter((data: TProgrammedMessage) => data.id !== result.id), result]
          return updatedData
        })
      }
      queryClient.setQueryData(['programmed-message', project.id, messageId], () => result)
      notify(
        {
          title: t('pages.configuration.messages.submit-success-title', { isUpdating: true }),
          message: t('pages.configuration.messages.submit-success-message'),
          appearance: 'SUCCESS',
        },
        15000,
      )
      navigate(backUrl)
    } catch (err: any) {
      if (isConflictError(err)) {
        // the reloaded message is not editable anymore: the update page notifies the reason and redirects
        queryClient.invalidateQueries({ queryKey: ['programmed-message', project.id, messageId] })
        queryClient.invalidateQueries({ queryKey: ['programmed-messages', project.id] })
        queryClient.invalidateQueries({ queryKey: ['followup-self-configuration', project.id, followupId] })
      } else {
        notify(
          {
            title: t('pages.configuration.messages.submit-error', { isUpdating: true }),
            message: err,
            appearance: 'ERROR',
          },
          15000,
        )
      }
    } finally {
      setTimeout(() => {
        setIsButtonLoading(false)
      }, 2000)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProgrammedMessageForm setIsButtonDisabled={setIsButtonDisabled} />

        <div className="c-mt-8 c-pb-20 c-flex c-justify-center c-gap-4">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            type="button"
            isDisabled={isButtonDisabled}
            isLoading={isButtonLoading}
            onClick={() => navigate(backUrl)}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton type="submit" isDisabled={isButtonDisabled} isLoading={isButtonLoading}>
            {t('pages.configuration.messages.validate-changes')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}

export { ProgrammedMessageForm, AddProgrammedMessageForm, UpdateProgrammedMessageForm }
